import { getFontStack } from './utils/fonts.js';
import { renderTable } from './shapes/table.js';
import { renderChart } from './shapes/chart.js';
import { renderVideo, renderAudio } from './shapes/video.js';
import { renderDiagram } from './shapes/diagram.js';
import { applyFill } from './shapes/gradient.js';
import { EMFRenderer } from './emf-renderer.js';
import log from './log.js';
export class SlideRenderer {
    constructor(canvas, options = {}) {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Failed to get canvas context');
        }
        this.ctx = ctx;
        this.scale = options.scale || 1;
        if (options.width && options.height) {
            canvas.width = options.width * this.scale;
            canvas.height = options.height * this.scale;
            const sourceWidth = options.slideWidth || 960;
            this.ptScale = options.width / sourceWidth;
            log.info('Canvas dimensions:', {
                canvasWidth: canvas.width,
                canvasHeight: canvas.height,
                optionsWidth: options.width,
                optionsHeight: options.height,
                slideWidth: options.slideWidth,
                slideHeight: options.slideHeight,
                scale: this.scale,
                ptScale: this.ptScale,
            });
        }
        else {
            this.ptScale = 1;
        }
        this.ctx.scale(this.scale, this.scale);
    }
    async renderSlide(slide, renderMode) {
        this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
        this.renderBackground(slide);
        const shapesToRender = this.getShapesForRenderMode(slide, renderMode || 'complete');
        for (const shape of shapesToRender) {
            await this.renderShape(shape);
        }
    }
    getShapesForRenderMode(slide, renderMode) {
        switch (renderMode) {
            case 'master-only':
                return slide.masterShapes || [];
            case 'layout-only':
                return slide.layoutShapes || [];
            case 'slide-only':
                return slide.slideShapes || [];
            case 'master-layout':
                return [
                    ...(slide.masterShapes || []),
                    ...(slide.layoutShapes || []),
                ];
            case 'layout-slide':
                return [
                    ...(slide.layoutShapes || []),
                    ...(slide.slideShapes || []),
                ];
            case 'complete':
            default:
                return slide.shapes;
        }
    }
    renderBackground(slide) {
        const canvasWidth = this.ctx.canvas.width / this.scale;
        const canvasHeight = this.ctx.canvas.height / this.scale;
        if (!slide.background) {
            this.ctx.fillStyle = '#ffffff';
            this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);
            return;
        }
        if (typeof slide.background === 'object' && 'type' in slide.background && 'stops' in slide.background) {
            const gradient = slide.background;
            let canvasGradient;
            if (gradient.type === 'linear') {
                const angle = (gradient.angle || 0) * Math.PI / 180;
                const centerX = canvasWidth / 2;
                const centerY = canvasHeight / 2;
                const length = Math.sqrt(canvasWidth * canvasWidth + canvasHeight * canvasHeight) / 2;
                const x1 = centerX - Math.cos(angle) * length;
                const y1 = centerY - Math.sin(angle) * length;
                const x2 = centerX + Math.cos(angle) * length;
                const y2 = centerY + Math.sin(angle) * length;
                canvasGradient = this.ctx.createLinearGradient(x1, y1, x2, y2);
            }
            else {
                const centerX = canvasWidth * (gradient.focusX || 0.5);
                const centerY = canvasHeight * (gradient.focusY || 0.5);
                const radius = Math.max(canvasWidth, canvasHeight) / 2;
                canvasGradient = this.ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
            }
            for (const stop of gradient.stops) {
                const color = stop.alpha !== undefined
                    ? this.hexToRGBA(stop.color, stop.alpha)
                    : stop.color;
                canvasGradient.addColorStop(stop.position, color);
            }
            this.ctx.fillStyle = canvasGradient;
            this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        }
        else {
            this.ctx.fillStyle = this.colorToString(slide.background);
            this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        }
    }
    hexToRGBA(hex, alpha) {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    async renderShape(shape) {
        this.ctx.save();
        try {
            switch (shape.type) {
                case 'rectangle':
                    this.renderRectangle(shape);
                    break;
                case 'circle':
                    this.renderCircle(shape);
                    break;
                case 'text':
                    this.renderText(shape);
                    break;
                case 'image':
                    await this.renderImage(shape);
                    break;
                case 'line':
                    this.renderLine(shape);
                    break;
                case 'table':
                    renderTable(this.ctx, shape, this.ptScale);
                    break;
                case 'chart':
                    renderChart(this.ctx, shape, this.ptScale);
                    break;
                case 'video':
                    renderVideo(this.ctx, shape, this.ptScale);
                    break;
                case 'audio':
                    renderAudio(this.ctx, shape, this.ptScale);
                    break;
                case 'diagram':
                    renderDiagram(this.ctx, shape, this.ptScale);
                    break;
                case 'group':
                    await this.renderGroup(shape);
                    break;
                case 'shape':
                    this.renderCustomShape(shape);
                    break;
                default:
                    log.warn('Unknown shape type', { type: shape.type });
            }
        }
        finally {
            this.ctx.restore();
        }
    }
    async renderGroup(shape) {
        if (!shape.children) {
            return;
        }
        log.info(`renderGroup: children=${shape.children.length}`);
        this.ctx.save();
        this.ctx.translate(shape.position.x * this.ptScale, shape.position.y * this.ptScale);
        for (const child of shape.children) {
            await this.renderShape(child);
        }
        this.ctx.restore();
    }
    renderCustomShape(shape) {
        if (shape.geometry && !shape.customGeometry) {
            this.renderPresetGeometry(shape);
            return;
        }
        if (!shape.customGeometry) {
            log.warn('Shape type is "shape" but no customGeometry or geometry provided');
            return;
        }
        const x = shape.position.x * this.ptScale;
        const y = shape.position.y * this.ptScale;
        const width = shape.size.width * this.ptScale;
        const height = shape.size.height * this.ptScale;
        const geom = shape.customGeometry;
        const scaleX = width / geom.width;
        const scaleY = height / geom.height;
        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.beginPath();
        for (const path of geom.paths) {
            for (const cmd of path) {
                switch (cmd.type) {
                    case 'M': {
                        const mx = cmd.points[0] * scaleX;
                        const my = cmd.points[1] * scaleY;
                        this.ctx.moveTo(mx, my);
                        break;
                    }
                    case 'L': {
                        const lx = cmd.points[0] * scaleX;
                        const ly = cmd.points[1] * scaleY;
                        this.ctx.lineTo(lx, ly);
                        break;
                    }
                    case 'C': {
                        const cp1x = cmd.points[0] * scaleX;
                        const cp1y = cmd.points[1] * scaleY;
                        const cp2x = cmd.points[2] * scaleX;
                        const cp2y = cmd.points[3] * scaleY;
                        const x = cmd.points[4] * scaleX;
                        const y = cmd.points[5] * scaleY;
                        this.ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
                        break;
                    }
                    case 'Z': {
                        this.ctx.closePath();
                        break;
                    }
                }
            }
        }
        if (shape.fill) {
            this.ctx.fillStyle = this.colorToString(shape.fill);
            this.ctx.fill('evenodd');
        }
        if (shape.stroke) {
            this.ctx.strokeStyle = this.colorToString(shape.stroke);
            this.ctx.lineWidth = (shape.strokeWidth || 1) * this.ptScale;
            this.ctx.stroke();
        }
        this.ctx.restore();
    }
    renderPresetGeometry(shape) {
        const x = shape.position.x * this.ptScale;
        const y = shape.position.y * this.ptScale;
        const width = shape.size.width * this.ptScale;
        const height = shape.size.height * this.ptScale;
        this.ctx.beginPath();
        switch (shape.geometry) {
            case 'rect':
                this.ctx.rect(x, y, width, height);
                break;
            case 'ellipse':
                const centerX = x + width / 2;
                const centerY = y + height / 2;
                this.ctx.ellipse(centerX, centerY, width / 2, height / 2, 0, 0, 2 * Math.PI);
                break;
            case 'roundRect':
            default:
                const radius = 20 * this.ptScale;
                this.ctx.roundRect(x, y, width, height, radius);
                break;
        }
        if (shape.fill) {
            this.ctx.fillStyle = this.colorToString(shape.fill);
            this.ctx.fill();
        }
        if (shape.stroke) {
            this.ctx.strokeStyle = this.colorToString(shape.stroke);
            this.ctx.lineWidth = (shape.strokeWidth || 1) * this.ptScale;
            this.ctx.stroke();
        }
    }
    wrapText(text, maxWidth, fontSize, fontFamily) {
        const font = `${fontSize}px ${fontFamily}`;
        this.ctx.font = font;
        const lines = [];
        const paragraphs = text.split('\n');
        for (const paragraph of paragraphs) {
            if (!paragraph.trim()) {
                lines.push('');
                continue;
            }
            const words = paragraph.split(' ');
            let currentLine = '';
            for (const word of words) {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                const metrics = this.ctx.measureText(testLine);
                if (metrics.width > maxWidth && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                }
                else {
                    currentLine = testLine;
                }
            }
            if (currentLine) {
                lines.push(currentLine);
            }
        }
        return lines;
    }
    renderRectangle(shape) {
        const x = shape.position.x * this.ptScale;
        const y = shape.position.y * this.ptScale;
        const width = shape.size.width * this.ptScale;
        const height = shape.size.height * this.ptScale;
        if (shape.geometry === 'ellipse') {
            const centerX = x + width / 2;
            const centerY = y + height / 2;
            this.ctx.beginPath();
            this.ctx.ellipse(centerX, centerY, width / 2, height / 2, 0, 0, 2 * Math.PI);
            if (shape.fill) {
                this.ctx.fillStyle = this.colorToString(shape.fill);
                this.ctx.fill();
            }
            if (shape.stroke) {
                this.ctx.strokeStyle = this.colorToString(shape.stroke);
                this.ctx.lineWidth = (shape.strokeWidth || 1) * this.ptScale;
                this.ctx.stroke();
            }
            return;
        }
        if (shape.fill) {
            applyFill(this.ctx, shape.fill, x, y, width, height);
            this.ctx.fillRect(x, y, width, height);
        }
        if (shape.stroke) {
            this.ctx.strokeStyle = this.colorToString(shape.stroke);
            this.ctx.lineWidth = (shape.strokeWidth || 1) * this.ptScale;
            this.ctx.strokeRect(x, y, width, height);
        }
    }
    renderCircle(shape) {
        const x = shape.position.x * this.ptScale;
        const y = shape.position.y * this.ptScale;
        const width = shape.size.width * this.ptScale;
        const height = shape.size.height * this.ptScale;
        const centerX = x + width / 2;
        const centerY = y + height / 2;
        const radiusX = width / 2;
        const radiusY = height / 2;
        this.ctx.beginPath();
        this.ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
        if (shape.fill) {
            applyFill(this.ctx, shape.fill, x, y, width, height);
            this.ctx.fill();
        }
        if (shape.stroke) {
            this.ctx.strokeStyle = this.colorToString(shape.stroke);
            this.ctx.lineWidth = (shape.strokeWidth || 1) * this.ptScale;
            this.ctx.stroke();
        }
    }
    renderText(shape) {
        if (shape.paragraphs && shape.paragraphs.length > 0) {
            const hasTextContent = shape.paragraphs.some(p => p.runs && p.runs.length > 0 && p.runs.some(r => r.text && r.text.trim().length > 0));
            if (!hasTextContent) {
                if (shape.fill) {
                    const x = shape.position.x * this.ptScale;
                    const y = shape.position.y * this.ptScale;
                    const width = shape.size.width * this.ptScale;
                    const height = shape.size.height * this.ptScale;
                    this.ctx.fillStyle = this.colorToString(shape.fill);
                    this.ctx.beginPath();
                    if (shape.geometry === 'rect' || !shape.geometry) {
                        this.ctx.rect(x, y, width, height);
                    }
                    else if (shape.geometry === 'ellipse') {
                        const centerX = x + width / 2;
                        const centerY = y + height / 2;
                        this.ctx.ellipse(centerX, centerY, width / 2, height / 2, 0, 0, 2 * Math.PI);
                    }
                    else if (shape.geometry === 'roundRect') {
                        const radius = 20 * this.ptScale;
                        this.ctx.roundRect(x, y, width, height, radius);
                    }
                    else {
                        this.ctx.rect(x, y, width, height);
                    }
                    this.ctx.fill();
                }
                return;
            }
            this.renderTextWithParagraphs(shape);
            return;
        }
        if (!shape.text && !shape.fill) {
            return;
        }
        const x = shape.position.x * this.ptScale;
        const y = shape.position.y * this.ptScale;
        const width = shape.size.width * this.ptScale;
        const height = shape.size.height * this.ptScale;
        const style = shape.textStyle || {};
        if (shape.fill) {
            this.ctx.fillStyle = this.colorToString(shape.fill);
            this.ctx.beginPath();
            if (shape.geometry === 'rect' || !shape.geometry) {
                this.ctx.rect(x, y, width, height);
            }
            else if (shape.geometry === 'ellipse') {
                const centerX = x + width / 2;
                const centerY = y + height / 2;
                this.ctx.ellipse(centerX, centerY, width / 2, height / 2, 0, 0, 2 * Math.PI);
            }
            else if (shape.geometry === 'roundRect') {
                const radius = 20 * this.ptScale;
                this.ctx.roundRect(x, y, width, height, radius);
            }
            else {
                this.ctx.rect(x, y, width, height);
            }
            this.ctx.fill();
        }
        if (!shape.text) {
            return;
        }
        const fontSize = (style.fontSize || 12) * this.ptScale;
        const fontFamily = getFontStack(style.fontFamily);
        const fontWeight = style.fontWeight || 'normal';
        const fontStyle = style.fontStyle || 'normal';
        let renderedText = shape.text;
        if (style.capitalization === 'all') {
            renderedText = renderedText.toUpperCase();
        }
        else if (style.capitalization === 'small') {
            renderedText = renderedText.toUpperCase();
        }
        this.ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
        if (style.color) {
            this.ctx.fillStyle = this.colorToString(style.color);
        }
        else {
            this.ctx.fillStyle = '#000000';
        }
        if (style.characterSpacing !== undefined) {
            this.ctx.letterSpacing = `${style.characterSpacing * this.ptScale}px`;
        }
        const margins = style.margins || {
            left: 7.2,
            right: 7.2,
            top: 3.6,
            bottom: 3.6,
        };
        const paddingLeft = (margins.left + (style.leftMargin || 0)) * this.ptScale;
        const paddingRight = (margins.right + (style.rightMargin || 0)) * this.ptScale;
        const paddingTop = margins.top * this.ptScale;
        const paddingBottom = margins.bottom * this.ptScale;
        this.ctx.textAlign = style.align || 'left';
        this.ctx.textBaseline = 'top';
        let textX = x;
        if (style.align === 'center') {
            textX = x + width / 2;
        }
        else if (style.align === 'right') {
            textX = x + width - paddingRight;
        }
        else {
            textX = x + paddingLeft;
        }
        const firstLineIndent = (style.firstLineIndent || 0) * this.ptScale;
        const maxWidth = width - paddingLeft - paddingRight;
        const lineSpacing = style.lineSpacing || 1.2;
        const lineHeight = fontSize * lineSpacing;
        const lines = this.wrapText(renderedText, maxWidth, fontSize, fontFamily);
        const spaceBefore = (style.spaceBefore || 0) * this.ptScale;
        const spaceAfter = (style.spaceAfter || 0) * this.ptScale;
        const totalTextHeight = lines.length * lineHeight + spaceBefore + spaceAfter;
        const availableHeight = height - paddingTop - paddingBottom;
        let textY = y + paddingTop + spaceBefore;
        if (style.verticalAnchor === 'middle') {
            textY = y + paddingTop + (availableHeight - totalTextHeight) / 2 + spaceBefore;
        }
        else if (style.verticalAnchor === 'bottom') {
            textY = y + height - paddingBottom - totalTextHeight + spaceBefore;
        }
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const lineY = textY + i * lineHeight;
            const lineX = i === 0 ? textX + firstLineIndent : textX;
            this.ctx.save();
            if (style.baseline !== undefined && style.baseline !== 0) {
                this.ctx.translate(0, -style.baseline * this.ptScale);
            }
            this.ctx.fillText(line, lineX, lineY);
            if (style.underline && style.underline !== 'none') {
                const metrics = this.ctx.measureText(line);
                const underlineY = lineY + fontSize * 1.1;
                const underlineThickness = Math.max(1, fontSize / 16);
                this.ctx.save();
                this.ctx.strokeStyle = this.ctx.fillStyle;
                this.ctx.lineWidth = underlineThickness;
                if (style.underline === 'double') {
                    this.ctx.beginPath();
                    this.ctx.moveTo(lineX, underlineY);
                    this.ctx.lineTo(lineX + metrics.width, underlineY);
                    this.ctx.stroke();
                    this.ctx.beginPath();
                    this.ctx.moveTo(lineX, underlineY + underlineThickness * 2);
                    this.ctx.lineTo(lineX + metrics.width, underlineY + underlineThickness * 2);
                    this.ctx.stroke();
                }
                else if (style.underline.includes('dash')) {
                    this.ctx.setLineDash([4 * this.ptScale, 2 * this.ptScale]);
                    this.ctx.beginPath();
                    this.ctx.moveTo(lineX, underlineY);
                    this.ctx.lineTo(lineX + metrics.width, underlineY);
                    this.ctx.stroke();
                    this.ctx.setLineDash([]);
                }
                else if (style.underline.includes('dot')) {
                    this.ctx.setLineDash([1 * this.ptScale, 2 * this.ptScale]);
                    this.ctx.beginPath();
                    this.ctx.moveTo(lineX, underlineY);
                    this.ctx.lineTo(lineX + metrics.width, underlineY);
                    this.ctx.stroke();
                    this.ctx.setLineDash([]);
                }
                else if (style.underline.includes('wavy')) {
                    this.ctx.setLineDash([3 * this.ptScale, 1.5 * this.ptScale]);
                    this.ctx.beginPath();
                    this.ctx.moveTo(lineX, underlineY);
                    this.ctx.lineTo(lineX + metrics.width, underlineY);
                    this.ctx.stroke();
                    this.ctx.setLineDash([]);
                }
                else {
                    this.ctx.beginPath();
                    this.ctx.moveTo(lineX, underlineY);
                    this.ctx.lineTo(lineX + metrics.width, underlineY);
                    this.ctx.stroke();
                }
                this.ctx.restore();
            }
            if (style.strikethrough && style.strikethrough !== 'none') {
                const metrics = this.ctx.measureText(line);
                const strikeY = lineY + fontSize * 0.5;
                const strikeThickness = Math.max(1, fontSize / 16);
                this.ctx.save();
                this.ctx.strokeStyle = this.ctx.fillStyle;
                this.ctx.lineWidth = strikeThickness;
                if (style.strikethrough === 'double') {
                    this.ctx.beginPath();
                    this.ctx.moveTo(lineX, strikeY - strikeThickness);
                    this.ctx.lineTo(lineX + metrics.width, strikeY - strikeThickness);
                    this.ctx.stroke();
                    this.ctx.beginPath();
                    this.ctx.moveTo(lineX, strikeY + strikeThickness);
                    this.ctx.lineTo(lineX + metrics.width, strikeY + strikeThickness);
                    this.ctx.stroke();
                }
                else {
                    this.ctx.beginPath();
                    this.ctx.moveTo(lineX, strikeY);
                    this.ctx.lineTo(lineX + metrics.width, strikeY);
                    this.ctx.stroke();
                }
                this.ctx.restore();
            }
            this.ctx.restore();
        }
        if (style.characterSpacing !== undefined) {
            this.ctx.letterSpacing = '0px';
        }
    }
    renderTextWithParagraphs(shape) {
        const x = shape.position.x * this.ptScale;
        const y = shape.position.y * this.ptScale;
        const width = shape.size.width * this.ptScale;
        const height = shape.size.height * this.ptScale;
        const style = shape.textStyle || {};
        const rotation = shape.rotation || 0;
        if (rotation !== 0) {
            this.ctx.save();
            const centerX = x + width / 2;
            const centerY = y + height / 2;
            this.ctx.translate(centerX, centerY);
            this.ctx.rotate((rotation * Math.PI) / 180);
            this.ctx.translate(-centerX, -centerY);
        }
        if (shape.fill) {
            this.ctx.fillStyle = this.colorToString(shape.fill);
            this.ctx.beginPath();
            if (shape.geometry === 'rect') {
                this.ctx.rect(x, y, width, height);
            }
            else if (shape.geometry === 'ellipse') {
                const centerX = x + width / 2;
                const centerY = y + height / 2;
                this.ctx.ellipse(centerX, centerY, width / 2, height / 2, 0, 0, 2 * Math.PI);
            }
            else {
                const radius = 20 * this.ptScale;
                this.ctx.roundRect(x, y, width, height, radius);
            }
            this.ctx.fill();
        }
        const margins = style.margins || { left: 7.2, right: 7.2, top: 3.6, bottom: 3.6 };
        const paddingLeft = (margins.left + (style.leftMargin || 0)) * this.ptScale;
        const paddingRight = (margins.right + (style.rightMargin || 0)) * this.ptScale;
        const paddingTop = margins.top * this.ptScale;
        let currentY = y + paddingTop;
        for (const paragraph of shape.paragraphs) {
            const pAlign = paragraph.align || style.align || 'left';
            const pLineSpacing = paragraph.lineSpacing || style.lineSpacing || 1.2;
            let textX = x + paddingLeft;
            if (pAlign === 'center') {
                textX = x + width / 2;
            }
            else if (pAlign === 'right') {
                textX = x + width - paddingRight;
            }
            if (paragraph.spaceBefore) {
                currentY += paragraph.spaceBefore * this.ptScale;
            }
            let bulletWidth = 0;
            let bulletChar = undefined;
            let bulletFontSize = (style.fontSize || 12) * this.ptScale;
            let bulletColor = '#000000';
            if (paragraph.bullet && paragraph.bullet.type === 'char' && paragraph.bullet.char) {
                bulletChar = paragraph.bullet.char;
                const bulletFontFamily = paragraph.bullet.font
                    ? getFontStack(paragraph.bullet.font)
                    : getFontStack(style.fontFamily);
                if (paragraph.bullet.size !== undefined) {
                    bulletFontSize *= paragraph.bullet.size;
                }
                if (paragraph.bullet.color) {
                    bulletColor = paragraph.bullet.color;
                }
                this.ctx.font = `normal normal ${bulletFontSize}px ${bulletFontFamily}`;
                bulletWidth = this.ctx.measureText(bulletChar + ' ').width;
            }
            const maxWidth = width - paddingLeft - paddingRight - bulletWidth;
            let lineX = textX + (paragraph.firstLineIndent || 0) * this.ptScale + bulletWidth;
            let currentLineRuns = [];
            let currentLineWidth = 0;
            let maxRunHeight = 0;
            let isFirstLine = true;
            const renderLine = () => {
                if (currentLineRuns.length === 0)
                    return;
                if (isFirstLine && bulletChar) {
                    const bulletFontFamily = paragraph.bullet?.font
                        ? getFontStack(paragraph.bullet.font)
                        : getFontStack(style.fontFamily);
                    this.ctx.save();
                    this.ctx.font = `normal normal ${bulletFontSize}px ${bulletFontFamily}`;
                    this.ctx.fillStyle = bulletColor;
                    this.ctx.textAlign = 'left';
                    this.ctx.textBaseline = 'top';
                    const bulletX = textX + (paragraph.firstLineIndent || 0) * this.ptScale;
                    this.ctx.fillText(bulletChar, bulletX, currentY);
                    this.ctx.restore();
                }
                let totalLineWidth = 0;
                if (pAlign === 'right') {
                    for (const { run, text: runText } of currentLineRuns) {
                        const runFontSize = (run.fontSize || style.fontSize || 12) * this.ptScale;
                        const runFontFamily = getFontStack(run.fontFamily || style.fontFamily);
                        const runBold = run.bold ? 'bold' : 'normal';
                        const runItalic = run.italic ? 'italic' : 'normal';
                        this.ctx.font = `${runItalic} ${runBold} ${runFontSize}px ${runFontFamily}`;
                        if (run.characterSpacing !== undefined) {
                            this.ctx.letterSpacing = `${run.characterSpacing * this.ptScale}px`;
                        }
                        totalLineWidth += this.ctx.measureText(runText).width;
                        if (run.characterSpacing !== undefined) {
                            this.ctx.letterSpacing = '0px';
                        }
                    }
                }
                let renderX = pAlign === 'right' ? lineX - totalLineWidth : lineX;
                for (const { run, text: runText } of currentLineRuns) {
                    const runFontSize = (run.fontSize || style.fontSize || 12) * this.ptScale;
                    const runFontFamily = getFontStack(run.fontFamily || style.fontFamily);
                    const runBold = run.bold ? 'bold' : 'normal';
                    const runItalic = run.italic ? 'italic' : 'normal';
                    this.ctx.font = `${runItalic} ${runBold} ${runFontSize}px ${runFontFamily}`;
                    this.ctx.textAlign = 'left';
                    this.ctx.textBaseline = 'top';
                    const textColor = run.color || style.color;
                    if (textColor) {
                        if (typeof textColor === 'object' && 'type' in textColor && 'stops' in textColor) {
                            log.info('[DEBUG] Detected gradient text color', { text: runText.substring(0, 20), gradient: textColor });
                            const textMetrics = this.ctx.measureText(runText);
                            const textHeight = runFontSize;
                            applyFill(this.ctx, textColor, renderX, currentY, textMetrics.width, textHeight);
                        }
                        else {
                            const runColorStr = this.colorToString(textColor);
                            this.ctx.fillStyle = runColorStr;
                        }
                    }
                    else {
                        this.ctx.fillStyle = '#000000';
                    }
                    if (run.characterSpacing !== undefined) {
                        this.ctx.letterSpacing = `${run.characterSpacing * this.ptScale}px`;
                    }
                    this.ctx.save();
                    if (run.baseline !== undefined && run.baseline !== 0) {
                        this.ctx.translate(0, -run.baseline * this.ptScale);
                    }
                    this.ctx.fillText(runText, renderX, currentY);
                    this.ctx.restore();
                    const metrics = this.ctx.measureText(runText);
                    renderX += metrics.width;
                    if (run.characterSpacing !== undefined) {
                        this.ctx.letterSpacing = '0px';
                    }
                    maxRunHeight = Math.max(maxRunHeight, runFontSize);
                }
                currentY += maxRunHeight * pLineSpacing;
                currentLineRuns = [];
                currentLineWidth = 0;
                lineX = textX + bulletWidth;
                maxRunHeight = 0;
                isFirstLine = false;
            };
            for (const run of paragraph.runs) {
                if (paragraph.bullet && run.text.trim() === paragraph.bullet.char?.trim()) {
                    continue;
                }
                const runFontSize = (run.fontSize || style.fontSize || 12) * this.ptScale;
                const runFontFamily = getFontStack(run.fontFamily || style.fontFamily);
                const runBold = run.bold ? 'bold' : 'normal';
                const runItalic = run.italic ? 'italic' : 'normal';
                let runText = run.text;
                if (run.capitalization === 'all' || style.capitalization === 'all') {
                    runText = runText.toUpperCase();
                }
                this.ctx.font = `${runItalic} ${runBold} ${runFontSize}px ${runFontFamily}`;
                const words = runText.split(/(\s+)/);
                for (const word of words) {
                    if (!word)
                        continue;
                    const wordWidth = this.ctx.measureText(word).width;
                    if (currentLineWidth + wordWidth > maxWidth && currentLineRuns.length > 0) {
                        renderLine();
                    }
                    currentLineRuns.push({ run, text: word });
                    currentLineWidth += wordWidth;
                    maxRunHeight = Math.max(maxRunHeight, runFontSize);
                }
            }
            if (shape.text && shape.text.includes('Total sample')) {
                log.info(`[DEBUG] About to call renderLine, currentLineRuns.length=${currentLineRuns.length}`);
            }
            renderLine();
            if (paragraph.spaceAfter) {
                currentY += paragraph.spaceAfter * this.ptScale;
            }
        }
        if (rotation !== 0) {
            this.ctx.restore();
        }
    }
    async renderImage(shape) {
        if (!shape.imageUrl) {
            return;
        }
        const x = shape.position.x * this.ptScale;
        const y = shape.position.y * this.ptScale;
        const width = shape.size.width * this.ptScale;
        const height = shape.size.height * this.ptScale;
        const rotation = shape.rotation || 0;
        if (shape.imageUrl.startsWith('unsupported:')) {
            this.drawImagePlaceholder(x, y, width, height);
            return;
        }
        if (typeof Image === 'undefined' && shape.imageUrl.includes('application/octet-stream')) {
            const base64Data = shape.imageUrl.split(',')[1];
            if (base64Data) {
                const Buffer = (await import('buffer')).Buffer;
                const buffer = Buffer.from(base64Data, 'base64');
                const magic = buffer.slice(0, 4);
                if (magic[0] === 0x01 && magic[1] === 0x00 && magic[2] === 0x00 && magic[3] === 0x00) {
                    try {
                        await this.renderEMFImage(shape.imageUrl, x, y, width, height);
                        return;
                    }
                    catch (err) {
                        log.error('Failed to render EMF image', { error: err });
                        this.drawImagePlaceholder(x, y, width, height);
                        return;
                    }
                }
            }
        }
        if (typeof Image !== 'undefined' && shape.imageUrl.includes('application/octet-stream')) {
            const base64Data = shape.imageUrl.split(',')[1];
            if (base64Data) {
                const binaryString = atob(base64Data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                if (bytes[0] === 0x01 && bytes[1] === 0x00 && bytes[2] === 0x00 && bytes[3] === 0x00) {
                    this.drawImagePlaceholder(x, y, width, height);
                    return;
                }
            }
        }
        if (shape.imageUrl.startsWith('data:image/svg+xml')) {
            try {
                const base64Data = shape.imageUrl.split(',')[1];
                const Buffer = (await import('buffer')).Buffer;
                const svgString = Buffer.from(base64Data, 'base64').toString('utf-8');
                const { Canvg, presets } = await import('canvg');
                const { DOMParser } = await import('@xmldom/xmldom');
                const { fetch } = await import('cross-fetch');
                const canvas = await import('canvas');
                this.ctx.save();
                this.ctx.translate(x, y);
                const preset = presets.node({
                    DOMParser,
                    canvas,
                    fetch,
                });
                const canvg = await Canvg.from(this.ctx, svgString, {
                    ...preset,
                    ignoreDimensions: true,
                    scaleWidth: width,
                    scaleHeight: height,
                });
                await canvg.render();
                this.ctx.restore();
                return;
            }
            catch (err) {
                log.error('Failed to render SVG with canvg', { error: err });
                this.drawImagePlaceholder(x, y, width, height);
                return;
            }
        }
        try {
            if (typeof Image === 'undefined' && this.ctx.canvas) {
                try {
                    const { loadImage } = await import('canvas');
                    const img = await loadImage(shape.imageUrl);
                    if (rotation !== 0) {
                        this.ctx.save();
                        const centerX = x + width / 2;
                        const centerY = y + height / 2;
                        this.ctx.translate(centerX, centerY);
                        this.ctx.rotate((rotation * Math.PI) / 180);
                        this.ctx.drawImage(img, -width / 2, -height / 2, width, height);
                        this.ctx.restore();
                    }
                    else {
                        this.ctx.drawImage(img, x, y, width, height);
                    }
                    return;
                }
                catch (err) {
                    log.error('Failed to load image with canvas.loadImage', { error: err });
                    this.drawImagePlaceholder(x, y, width, height);
                    return;
                }
            }
            const img = new Image();
            const imageUrl = shape.imageUrl;
            await new Promise((resolve) => {
                img.onload = () => {
                    try {
                        if (rotation !== 0) {
                            this.ctx.save();
                            const centerX = x + width / 2;
                            const centerY = y + height / 2;
                            this.ctx.translate(centerX, centerY);
                            this.ctx.rotate((rotation * Math.PI) / 180);
                            this.ctx.drawImage(img, -width / 2, -height / 2, width, height);
                            this.ctx.restore();
                        }
                        else {
                            this.ctx.drawImage(img, x, y, width, height);
                        }
                        resolve();
                    }
                    catch (err) {
                        log.error('Failed to draw image to canvas', { error: err });
                        this.drawImagePlaceholder(x, y, width, height);
                        resolve();
                    }
                };
                img.onerror = (err) => {
                    log.error('Failed to load image', { error: err, imageUrl });
                    this.drawImagePlaceholder(x, y, width, height);
                    resolve();
                };
                img.src = imageUrl;
            });
        }
        catch (err) {
            log.error('Failed to render image', { error: err });
            this.drawImagePlaceholder(x, y, width, height);
        }
    }
    async renderEMFImage(dataUri, x, y, width, height) {
        const base64Data = dataUri.split(',')[1];
        if (!base64Data) {
            throw new Error('Invalid data URI format');
        }
        const binaryString = Buffer.from(base64Data, 'base64');
        const buffer = binaryString.buffer.slice(binaryString.byteOffset, binaryString.byteOffset + binaryString.byteLength);
        const firstBytes = new Uint8Array(buffer).slice(0, 16);
        log.info('EMF first bytes:', Array.from(firstBytes).map(b => b.toString(16).padStart(2, '0')).join(' '));
        const { createCanvas } = await import('canvas');
        const emfCanvas = createCanvas(Math.ceil(width), Math.ceil(height));
        const emfCtx = emfCanvas.getContext('2d');
        const emfRenderer = new EMFRenderer(emfCtx);
        emfRenderer.render(buffer);
        this.ctx.drawImage(emfCanvas, x, y, width, height);
    }
    drawImagePlaceholder(x, y, width, height) {
        this.ctx.fillStyle = '#cccccc';
        this.ctx.fillRect(x, y, width, height);
        this.ctx.strokeStyle = '#999999';
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(x, y, width, height);
    }
    renderLine(shape) {
        const x = shape.position.x * this.ptScale;
        const y = shape.position.y * this.ptScale;
        const width = shape.size.width * this.ptScale;
        const height = shape.size.height * this.ptScale;
        this.ctx.beginPath();
        this.ctx.moveTo(x, y);
        this.ctx.lineTo(x + width, y + height);
        if (shape.stroke) {
            this.ctx.strokeStyle = this.colorToString(shape.stroke);
        }
        else {
            this.ctx.strokeStyle = '#000000';
        }
        this.ctx.lineWidth = (shape.strokeWidth || 1) * this.ptScale;
        this.ctx.stroke();
    }
    colorToString(color) {
        if (typeof color === 'string') {
            return color;
        }
        if ('type' in color && 'stops' in color) {
            return '#000000';
        }
        const a = color.a !== undefined ? color.a : 1;
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${a})`;
    }
}
//# sourceMappingURL=renderer.js.map